import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError, useNavigate, useLocation } from 'react-router-dom';
import { Container, Title, Text, Button, Stack, Code, Group } from '@mantine/core';
import { isPostHogEnabled, posthog } from '@/lib/posthog';
import { hasQuoteToken, isPublicQuoteRoute } from '@/lib/publicRoutes';
import { CustomerQuoteUnavailable } from '@/components/customer/CustomerQuoteUnavailable';
import { reportEventRouteError } from '@/features/events/technicalReports';

export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    reportEventRouteError(pathname, error);
    if (!isPostHogEnabled) return;
    if (error instanceof Error) {
      posthog.captureException(error);
      return;
    }
    // 404s are navigation noise, not defects; report other route responses (failed loaders/actions)
    if (isRouteErrorResponse(error) && error.status !== 404) {
      posthog.captureException(new Error(`Route error ${error.status}: ${error.statusText || 'unknown'}`));
    }
  }, [error, pathname]);

  if (isPublicQuoteRoute(pathname)) {
    return <CustomerQuoteUnavailable hasToken={hasQuoteToken(pathname)} />;
  }

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found';
      message = "The page you're looking for doesn't exist or has been moved.";
    } else {
      title = `${error.status} ${error.statusText}`;
      message = error.data?.message || message;
    }
  }

  return (
    <Container size="sm" py="xl">
      <Stack align="center" gap="md">
        <Title order={1}>{title}</Title>
        <Text c="dimmed" ta="center" maw={500}>
          {message}
        </Text>
        {import.meta.env.DEV && error instanceof Error && (
          <Code block w="100%">
            {error.stack || error.message}
          </Code>
        )}
        <Group>
          <Button variant="default" onClick={() => window.location.reload()}>Try again</Button>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </Group>
      </Stack>
    </Container>
  );
}
